import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Center,
  Group,
  Loader,
  Menu,
  MultiSelect,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconChecks,
  IconChevronDown,
  IconChevronRight,
  IconPlus,
  IconReceipt,
  IconSearch,
  IconArrowRight,
  IconSend,
  IconX,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import LogExpenseForm from "./LogExpenseForm";
import "./BudgetTab.css";

const money = n =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Progressive MM/DD/YYYY entry. A native date input is three tab stops with a
 * built-in picker button, so this is a plain text field that inserts the
 * slashes as you go — one Tab moves to the next column.
 */
const formatDateInput = raw => {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/** MM/DD/YYYY -> the ISO date the API stores, or null when incomplete. */
const parseDateInput = display => {
  const digits = String(display ?? "").replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const month = Number(digits.slice(0, 2));
  const day = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4));
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    return null;
  }
  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  // Rejects the impossible ones a range check misses, like 02/31.
  const probe = new Date(`${iso}T00:00:00`);
  return probe.getUTCDate() === day || probe.getDate() === day ? iso : null;
};

/** Today, in the format the field displays. */
const todayDisplay = () => {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, "0")}/${String(
    now.getDate()
  ).padStart(2, "0")}/${now.getFullYear()}`;
};

/**
 * Cents-first amount entry: digits fill from the right, so the field always
 * reads as real money while you type — 1 → $0.01, 12 → $0.12, 1234 → $12.34.
 * There is no half-typed state to settle afterwards and the caret always sits
 * at the end, so nothing to fight.
 */
const formatAmountInput = raw => {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "")
    .slice(0, 11);
  if (!digits) return "";
  return `$${(Number(digits) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** The number behind a formatted amount field. */
const parseAmountInput = raw =>
  Number(String(raw ?? "").replace(/[^0-9.]/g, "")) || 0;

const fullName = r =>
  `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
  `#${r.user_id ?? r.id}`;

/** "Aug 19, 2026" — compact enough for a column, still unambiguous alone. */
const formatDayShort = iso => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const vendorInitials = vendor => {
  const words = String(vendor || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Stable colour per vendor so the same merchant keeps the same chip across
 * reloads — hashed from the name rather than the row index, which would
 * reshuffle whenever the list is filtered.
 */
const AVATAR_COLORS = [
  "#3B6FD4",
  "#953B16",
  "#0F766E",
  "#7C3AED",
  "#B45309",
  "#9B2C2C",
  "#166534",
  "#475569",
];
const avatarColor = vendor => {
  const name = String(vendor || "?");
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 9973;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

/**
 * The one state a row is in, read off the two stored fields plus the
 * reconciliation timestamp.
 *
 * "Logged" and "Reviewed" share an underlying APPROVED: a bucket that waives
 * approval auto-approves its expenses so they count as real spend, but nobody
 * actually reviewed them, so calling those Reviewed would claim a sign-off that
 * never happened. The budget's own rule is what separates the two.
 */
const statusOf = e => {
  if (e.approval_status === "VOID") return { label: "Void", cls: "is-void" };
  if (e.approval_status === "REJECTED")
    return { label: "Rejected", cls: "is-rejected" };

  const governed = e.budget_line?.requires_approval !== false;

  if (e.approval_status === "DRAFT") {
    return governed
      ? { label: "Needs approval", cls: "is-needs-approval" }
      : { label: "Logged", cls: "is-logged" };
  }
  if (e.approval_status === "SUBMITTED")
    return { label: "Pending", cls: "is-pending" };

  if (e.reconciled_at) return { label: "Reconciled", cls: "is-reconciled" };
  return governed
    ? { label: "Reviewed", cls: "is-reviewed" }
    : { label: "Logged", cls: "is-logged" };
};

/**
 * A blank inline row. The bearer defaults to the company because that is the
 * common case; anything needing a real split uses the full form instead.
 */
/**
 * Splits a dollar amount into `n` equal parts, in cents, handing the leftover
 * cents to the earliest shares. Bearers have to total the expense exactly, so
 * a remainder cannot be rounded away — $10 across 3 people is 3.34/3.33/3.33,
 * never 3.33 three times.
 */
const evenSplit = (amount, n) => {
  const total = Math.round(amount * 100);
  const base = Math.floor(total / n);
  const extra = total - base * n;
  return Array.from(
    { length: n },
    (_, i) => (base + (i < extra ? 1 : 0)) / 100
  );
};

/**
 * The ledger's columns. Date, Amount, Receipt, Status and the open chevron have
 * known content, so they stay fixed; the seven text columns share the slack.
 * Dragging a header edge swaps that column's entry for a pixel width, which is
 * why these are strings rather than numbers.
 */
const DEFAULT_TRACKS = [
  "112px",
  ...Array(7).fill("minmax(130px, 1fr)"),
  "148px",
  "76px",
  "150px",
  "34px",
];

/** Narrow enough to be useful, wide enough to still read as a column. */
const MIN_COL = 72;

const isFlexible = track => track.endsWith("fr)");

/**
 * One template for the header, the entry row and every data row, so they cannot
 * drift apart. A focused field takes the slack and the other *flexible* columns
 * give it up — a column the user has dragged to a width keeps that width, since
 * they asked for it explicitly.
 */
const buildTemplate = (tracks, focusCol) => {
  if (focusCol == null || !isFlexible(tracks[focusCol]))
    return tracks.join(" ");
  return tracks
    .map((track, i) => {
      if (!isFlexible(track)) return track;
      return i === focusCol ? "minmax(300px, 3fr)" : "minmax(74px, 0.55fr)";
    })
    .join(" ");
};

const HEAD_COLUMNS = [
  { label: "Date", cap: "bt-cap-left" },
  { label: "Vendor" },
  { label: "Source" },
  { label: "Budget" },
  { label: "Category" },
  { label: "Description" },
  { label: "Expensed to" },
  { label: "Spent on" },
  { label: "Amount" },
  { label: "Receipt" },
  { label: "Status", cap: "bt-cap-status" },
  { label: "", cap: "bt-cap-open" },
];

const emptyDraft = () => ({
  incurred_on: todayDisplay(),
  vendor: "",
  description: "",
  sourceId: null,
  budgetLineId: null,
  categoryId: null,
  bearers: ["COMPANY"],
  spentOn: [],
  amount: "",
});

const SETTLEMENT_METHODS = [
  { value: "MANUAL", label: "Squared up outside Reppi" },
  { value: "PAYROLL_DEDUCTION", label: "Take it out of their pay" },
  { value: "REIMBURSEMENT", label: "Company paid them back" },
  { value: "OFFSET", label: "Netted against what they were owed" },
];

/**
 * The Budget tab: what was spent, who still owes for it, and what each rep has
 * cost. Three views because they answer three different questions — the split
 * that matters is deliberately not collapsed into one table.
 */
function BudgetTab() {
  const { getVerifiedJWT, user } = useAuth();
  const { showError, showSuccess } = useToast();

  const [view, setView] = useState("expenses");
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [byRep, setByRep] = useState([]);
  const [people, setPeople] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [budgetLines, setBudgetLines] = useState([]);
  const [orgTargets, setOrgTargets] = useState([]);
  const [search, setSearch] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleMethod, setSettleMethod] = useState("MANUAL");
  const [settleNote, setSettleNote] = useState("");
  const [busy, setBusy] = useState(false);
  // null | "single" | "bulk" — bulk keeps the row open after each save so a run
  // of entries never needs the mouse.
  const [mode, setMode] = useState(null);
  // Toggles the button's own appearance and nothing else yet. The endpoints it
  // will drive (/v1/spend/expenses/:id/reconcile and /unreconcile) exist, but
  // no row-level checking UI is built, so turning this on changes nothing.
  const [reconcileMode, setReconcileMode] = useState(false);
  const [tracks, setTracks] = useState(DEFAULT_TRACKS);
  // Index of the entry-row field being typed in, or null. Drives the widening.
  const [focusCol, setFocusCol] = useState(null);
  const template = useMemo(
    () => buildTemplate(tracks, focusCol),
    [tracks, focusCol]
  );
  // Read during a drag, which runs outside React and so cannot see the state.
  const ledgerRef = useRef(null);
  const tracksRef = useRef(tracks);
  const focusColRef = useRef(focusCol);
  tracksRef.current = tracks;
  focusColRef.current = focusCol;
  const firstFieldRef = useRef(null);
  const [newBudgetOpen, setNewBudgetOpen] = useState(false);
  /** "all", "none" (no budget), or a budget line id as a string. */
  const [budgetFilter, setBudgetFilter] = useState("all");
  const [newLine, setNewLine] = useState({
    name: "",
    amount: "",
    requiresApproval: true,
    requiresReceipt: false,
    viewerIds: [],
  });
  const [savingLine, setSavingLine] = useState(false);
  /** What is typed into the Budget picker on the entry row. */
  const [budgetSearch, setBudgetSearch] = useState("");
  const [savingRow, setSavingRow] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);

  const authedFetch = useCallback(
    async (path, init = {}) => {
      const jwt = await getVerifiedJWT();
      return fetch(getApiEndpoint(path), {
        ...init,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          ...(init.headers || {}),
        },
      });
    },
    [getVerifiedJWT]
  );

  /** Handled rejections come back as a bare JSON string, not an object. */
  const readError = async res => {
    const raw = await res.text();
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") return parsed;
      const msg = parsed.message;
      return Array.isArray(msg)
        ? msg.join(", ")
        : msg || "Something went wrong.";
    } catch {
      return raw.replace(/^"|"$/g, "") || "Something went wrong.";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      const qs = params.toString();

      const [
        expRes,
        ledRes,
        repRes,
        peopleRes,
        catRes,
        srcRes,
        lineRes,
        orgRes,
      ] = await Promise.all([
        authedFetch(`/v1/spend/expenses${qs ? `?${qs}` : ""}`),
        authedFetch("/v1/spend/expenses/ledger"),
        authedFetch("/v1/spend/expenses/by-rep"),
        authedFetch("/v1/spend/people"),
        authedFetch("/v1/spend/categories"),
        authedFetch("/v1/spend/sources"),
        authedFetch("/v1/spend/budget-lines"),
        authedFetch("/v1/spend/attribution-targets"),
      ]);

      if (expRes.ok) setExpenses((await expRes.json()).data || []);
      if (ledRes.ok) setLedger((await ledRes.json()).data || []);
      if (repRes.ok) setByRep((await repRes.json()).data || []);
      if (peopleRes.ok) setPeople((await peopleRes.json()).data || []);
      if (catRes.ok) setCategories((await catRes.json()).data || []);
      if (srcRes.ok) setSources((await srcRes.json()).data || []);
      if (lineRes.ok) setBudgetLines((await lineRes.json()).data || []);
      if (orgRes.ok) setOrgTargets((await orgRes.json()).data || []);
    } catch {
      showError("Could not load spend.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedFetch, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Opening the row should put the cursor in it — in bulk mode especially, the
  // whole point is never reaching for the mouse.
  useEffect(() => {
    if (mode) firstFieldRef.current?.focus();
  }, [mode]);

  const act = async (path, body, successMessage) => {
    setBusy(true);
    try {
      const res = await authedFetch(path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        showError(await readError(res));
        return false;
      }
      showSuccess(successMessage);
      await load();
      return true;
    } catch {
      showError("Something went wrong.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Saves the inline row and immediately clears it for the next entry — the
   * point of typing into the table is entering several without stopping.
   */
  const saveDraft = async () => {
    if (
      !draft.budgetLineId ||
      !draft.categoryId ||
      !parseAmountInput(draft.amount)
    ) {
      showError("Pick a budget and a category, and enter an amount.");
      return;
    }
    const incurredOn = parseDateInput(draft.incurred_on);
    if (!incurredOn) {
      showError("Enter a date as MM/DD/YYYY.");
      return;
    }
    setSavingRow(true);
    try {
      const amount = parseAmountInput(draft.amount);
      const bearerShares = evenSplit(amount, draft.bearers.length || 1);
      const spentOnShares = evenSplit(amount, draft.spentOn.length || 1);
      const payload = {
        budget_category_id: Number(draft.categoryId),
        amount,
        incurred_on: incurredOn,
        payment_timing: "IMMEDIATE",
        // Newer API versions derive the payer from the chosen source, so the
        // field is left out and the server decides. Older ones still require
        // it — `is_personal` on the sources response is the tell, since it
        // ships in the same release as the inference. Sending COMPANY there
        // keeps saves working until that deploy lands.
        ...(serverInfersPayer ? {} : { paid_by_party: "COMPANY" }),
        ...(draft.sourceId
          ? { payment_source_id: Number(draft.sourceId) }
          : {}),
        ...(draft.budgetLineId
          ? { budget_line_id: Number(draft.budgetLineId) }
          : {}),
        ...(draft.vendor.trim() ? { vendor: draft.vendor.trim() } : {}),
        ...(draft.description.trim()
          ? { description: draft.description.trim() }
          : {}),
        // Left out entirely when nothing is picked, which makes whoever paid
        // bear it — the server's own default, so we don't restate it here.
        ...(draft.bearers.length
          ? {
              bearers: draft.bearers.map((b, i) => ({
                bearer_party: b === "COMPANY" ? "COMPANY" : "USER",
                ...(b === "COMPANY" ? {} : { bearer_user_id: Number(b) }),
                split_basis: "FIXED",
                amount: bearerShares[i],
              })),
            }
          : {}),
        // "user:3" / "org:4" — one picker carrying two kinds of target.
        ...(draft.spentOn.length
          ? {
              attributions: draft.spentOn.map((t, i) =>
                t.startsWith("org:")
                  ? {
                      org_level_entity_id: Number(t.slice(4)),
                      amount: spentOnShares[i],
                    }
                  : { user_id: Number(t.slice(5)), amount: spentOnShares[i] }
              ),
            }
          : {}),
      };

      const res = await authedFetch("/v1/spend/expenses", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        showError(await readError(res));
        return;
      }
      showSuccess("Expense added.");
      // Keep the date so a run of same-day entries doesn't need re-typing.
      setDraft({ ...emptyDraft(), incurred_on: draft.incurred_on });
      await load();
      if (mode === "bulk") {
        // Straight back to the first field so the next row starts with a
        // keystroke, not a click.
        firstFieldRef.current?.focus();
      } else {
        setMode(null);
      }
    } catch {
      showError("Could not add this expense.");
    } finally {
      setSavingRow(false);
    }
  };

  const saveBudgetLine = async () => {
    if (!newLine.name.trim()) {
      showError("Give the budget a name.");
      return;
    }
    if (newLine.viewerIds.length === 0) {
      showError("Pick at least one person who can see this budget.");
      return;
    }
    setSavingLine(true);
    try {
      const res = await authedFetch("/v1/spend/budget-lines", {
        method: "POST",
        body: JSON.stringify({
          name: newLine.name.trim(),
          ...(parseAmountInput(newLine.amount)
            ? { allocated_amount: parseAmountInput(newLine.amount) }
            : {}),
          requires_approval: newLine.requiresApproval,
          requires_receipt: newLine.requiresReceipt,
          viewer_user_ids: newLine.viewerIds.map(Number),
        }),
      });
      if (!res.ok) {
        showError(await readError(res));
        return;
      }
      showSuccess("Budget added.");
      setNewBudgetOpen(false);
      setNewLine({
        name: "",
        amount: "",
        requiresApproval: true,
        requiresReceipt: false,
        viewerIds: [],
      });
      await load();
    } catch {
      showError("Could not add that budget.");
    } finally {
      setSavingLine(false);
    }
  };

  /**
   * The users.id behind this login. Expense people rows carry emails, the auth
   * context carries the login email; matching the two is how the browser learns
   * its own user id without the API shipping it in the token.
   */
  const myUserId = useMemo(() => {
    const email = String(user?.email || "").toLowerCase();
    if (!email) return null;
    const me = people.find(p => String(p.email || "").toLowerCase() === email);
    return me ? me.id : null;
  }, [people, user]);

  /**
   * Adds a budget from the entry row without leaving it. Visible to the
   * creator only until someone widens it; the row picks it up straight away.
   */
  const createBudgetInline = async rawName => {
    const name = String(rawName || "").trim();
    if (!name) return;
    if (!myUserId) {
      // Can't satisfy the viewer requirement blind — fall back to the form.
      setNewLine(l => ({ ...l, name }));
      setNewBudgetOpen(true);
      return;
    }
    setSavingLine(true);
    try {
      const res = await authedFetch("/v1/spend/budget-lines", {
        method: "POST",
        body: JSON.stringify({
          name,
          requires_approval: true,
          requires_receipt: false,
          viewer_user_ids: [myUserId],
        }),
      });
      if (!res.ok) {
        showError(await readError(res));
        return;
      }
      const created = (await res.json()).data;
      const lineRes = await authedFetch("/v1/spend/budget-lines");
      if (lineRes.ok) setBudgetLines((await lineRes.json()).data || []);
      if (created?.id) {
        setDraft(d => ({ ...d, budgetLineId: String(created.id) }));
      }
      setBudgetSearch("");
      showSuccess(`Budget "${name}" added.`);
    } catch {
      showError("Could not add that budget.");
    } finally {
      setSavingLine(false);
    }
  };

  const cancelDraft = () => {
    setDraft(emptyDraft());
    setMode(null);
  };

  /**
   * Drags one header edge. The pointer is captured on the handle so the drag
   * survives leaving the header — releasing over the page body still ends it,
   * and nothing else on the row sees the move events.
   */
  const startResize = (event, index) => {
    event.preventDefault();
    const handle = event.currentTarget;
    const ledger = ledgerRef.current;
    const startX = event.clientX;
    const startWidth = handle.parentElement.getBoundingClientRect().width;
    const base = tracksRef.current;
    let latest = base;
    let frame = 0;

    handle.classList.add("is-dragging");
    handle.setPointerCapture(event.pointerId);

    const paint = () => {
      frame = 0;
      ledger?.style.setProperty(
        "--bt-cols",
        buildTemplate(latest, focusColRef.current)
      );
    };

    const onMove = moveEvent => {
      const width = Math.max(
        MIN_COL,
        Math.round(startWidth + moveEvent.clientX - startX)
      );
      latest = [...base];
      latest[index] = `${width}px`;
      // Written straight to the DOM, and only once per frame. Calling setTracks
      // here re-laid out every row in the ledger on every pointer move, which is
      // what left the column trailing behind the cursor; React only hears about
      // the new width when the drag ends.
      if (!frame) frame = requestAnimationFrame(paint);
    };

    const onDone = () => {
      if (frame) cancelAnimationFrame(frame);
      paint();
      handle.classList.remove("is-dragging");
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onDone);
      handle.removeEventListener("pointercancel", onDone);
      setTracks(latest);
    };

    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onDone);
    handle.addEventListener("pointercancel", onDone);
  };

  /** Double-clicking an edge hands that one column back to the shared slack. */
  const resetColumn = index =>
    setTracks(current => {
      const next = [...current];
      next[index] = DEFAULT_TRACKS[index];
      return next;
    });

  const draftKeys = event => {
    // A picker with its list open owns both keys — Enter is choosing an option
    // and Escape is closing the list. That only became load-bearing once these
    // fields took several people: adding four bearers by keyboard is four
    // Enters, and every one of them used to save and clear the row instead.
    const open =
      event.target
        .closest?.("[aria-expanded]")
        ?.getAttribute("aria-expanded") === "true";
    if (open && (event.key === "Enter" || event.key === "Escape")) return;

    if (event.key === "Enter") {
      event.preventDefault();
      saveDraft();
    } else if (event.key === "Escape") {
      cancelDraft();
    }
  };

  /**
   * Receipts live in S3; the API hands back a short-lived signed URL rather
   * than a public link, so the row can only fetch one on demand.
   */
  const openReceipt = async id => {
    try {
      const res = await authedFetch(`/v1/spend/expenses/${id}/receipt`);
      if (!res.ok) {
        showError(await readError(res));
        return;
      }
      const body = await res.json();
      const url = body?.data?.url;
      if (!url) {
        showError("That receipt could not be opened.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      showError("That receipt could not be opened.");
    }
  };

  const voidExpense = async id => {
    const reason = window.prompt("Why is this being voided?");
    if (!reason) return;
    await act(`/v1/spend/expenses/${id}/void`, { reason }, "Expense voided.");
  };

  const rejectExpense = async id => {
    const reason = window.prompt("Why is this being rejected?");
    if (!reason) return;
    await act(
      `/v1/spend/expenses/${id}/reject`,
      { reason },
      "Expense rejected."
    );
  };

  const confirmSettle = async () => {
    if (!settleTarget) return;
    const ok = await act(
      `/v1/spend/bearers/${settleTarget.bearer.id}/settle`,
      {
        settlement_method: settleMethod,
        ...(settleNote.trim() ? { note: settleNote.trim() } : {}),
        ...(settleTarget.expense.selling_year_id
          ? { selling_year_id: settleTarget.expense.selling_year_id }
          : {}),
      },
      "Marked settled."
    );
    if (ok) {
      setSettleTarget(null);
      setSettleNote("");
      setSettleMethod("MANUAL");
    }
  };

  // ---- headline numbers -----------------------------------------------------

  const totals = useMemo(() => {
    const approved = expenses.filter(e => e.approval_status === "APPROVED");
    const committed = approved.reduce((s, e) => s + Number(e.amount || 0), 0);
    const paid = approved
      .filter(e => e.payment_status === "PAID")
      .reduce((s, e) => s + Number(e.amount || 0), 0);
    const awaiting = expenses.filter(
      e => e.approval_status === "SUBMITTED"
    ).length;
    const outstanding = ledger.reduce(
      (s, r) => s + Math.max(0, Number(r.owed_by_user || 0)),
      0
    );
    return { committed, paid, awaiting, outstanding };
  }, [expenses, ledger]);

  /** The ledger rows the picker in the first card lets through. */
  const visibleExpenses = useMemo(() => {
    if (budgetFilter === "all") return expenses;
    if (budgetFilter === "none") return expenses.filter(e => !e.budget_line_id);
    return expenses.filter(e => String(e.budget_line_id) === budgetFilter);
  }, [expenses, budgetFilter]);

  const selectedLine = useMemo(
    () =>
      budgetFilter === "all" || budgetFilter === "none"
        ? null
        : budgetLines.find(l => String(l.id) === budgetFilter) || null,
    [budgetLines, budgetFilter]
  );

  /**
   * The top row. "Budgeted" is the sum of every cap that has one; a budget
   * without a cap contributes nothing there but still counts as a budget.
   */
  const overview = useMemo(() => {
    const capped = budgetLines.filter(l => l.allocated_amount != null);
    const budgeted = capped.reduce(
      (sum, l) => sum + Number(l.allocated_amount || 0),
      0
    );
    const unbudgeted = expenses.filter(
      e => !e.budget_line_id && e.approval_status !== "VOID"
    ).length;

    let heroSpent = totals.committed;
    let heroCap = capped.length > 0 ? budgeted : null;
    if (selectedLine) {
      heroSpent = Number(selectedLine.committed || 0);
      heroCap =
        selectedLine.allocated_amount == null
          ? null
          : Number(selectedLine.allocated_amount);
    } else if (budgetFilter === "none") {
      heroSpent = visibleExpenses
        .filter(e => e.approval_status === "APPROVED")
        .reduce((sum, e) => sum + Number(e.amount || 0), 0);
      heroCap = null;
    }

    return {
      budgeted,
      unbudgeted,
      heroSpent,
      heroCap,
      heroLeft: heroCap == null ? null : heroCap - heroSpent,
    };
  }, [
    budgetLines,
    expenses,
    totals,
    selectedLine,
    budgetFilter,
    visibleExpenses,
  ]);

  const heroLabel =
    budgetFilter === "all"
      ? "All budgets"
      : budgetFilter === "none"
        ? "Unbudgeted"
        : selectedLine?.name || "All budgets";

  /**
   * Expense rows carry bearer_user_id but no name — the API deliberately does
   * not join the User entity, which would ship its auth hash to the browser.
   * Names come from the people endpoint instead.
   */
  /**
   * True when the API returns `is_personal` on payment sources, which is the
   * release that also made `paid_by_party` optional and inferred server-side.
   */
  const serverInfersPayer = useMemo(
    () => sources.some(src => Object.hasOwn(src, "is_personal")),
    [sources]
  );

  // Option lists for the row's searchable pickers. Values are strings because
  // Mantine's Select compares them as such.
  const peopleOptions = useMemo(
    () => people.map(p => ({ value: String(p.id), label: fullName(p) })),
    [people]
  );

  const partyOptions = useMemo(
    () => [{ value: "COMPANY", label: "Company" }, ...peopleOptions],
    [peopleOptions]
  );

  const sourceOptions = useMemo(
    () =>
      sources.map(src => ({
        value: String(src.id),
        label: src.last_four ? `${src.name} ••${src.last_four}` : src.name,
      })),
    [sources]
  );

  /**
   * "Spent on" reads down the org chart — each level from the top (Regional,
   * then Division, then Team) and individual reps last. Org entities are
   * prefixed `org:` so one Select can carry both kinds of target.
   */
  const spentOnOptions = useMemo(() => {
    const groups = orgTargets
      .filter(g => g.options?.length)
      .map(g => ({
        group: g.group,
        items: g.options.map(o => ({
          value: `org:${o.id}`,
          label: o.parent ? `${o.label} — ${o.parent}` : o.label,
        })),
      }));

    if (peopleOptions.length > 0) {
      groups.push({
        group: "Reps",
        items: peopleOptions.map(p => ({
          value: `user:${p.value}`,
          label: p.label,
        })),
      });
    }
    return groups;
  }, [orgTargets, peopleOptions]);

  const CREATE_BUDGET = "__create_budget__";

  const budgetOptions = useMemo(() => {
    const options = budgetLines.map(l => ({
      value: String(l.id),
      label: l.name,
    }));
    const typed = budgetSearch.trim();
    const exists = budgetLines.some(
      l => l.name.trim().toLowerCase() === typed.toLowerCase()
    );
    if (typed && !exists) {
      options.push({ value: CREATE_BUDGET, label: `Create "${typed}"` });
    }
    return options;
  }, [budgetLines, budgetSearch]);

  const categoryOptions = useMemo(
    () => categories.map(c => ({ value: String(c.id), label: c.name })),
    [categories]
  );

  const nameById = useMemo(() => {
    const map = new Map();
    people.forEach(p => map.set(p.id, fullName(p)));
    return map;
  }, [people]);

  const bearerName = b =>
    b.bearer_party === "COMPANY"
      ? "Company"
      : nameById.get(b.bearer_user_id) || `#${b.bearer_user_id}`;

  /** Who the spend was on; blank is honest for company overhead. */
  const attributionSummary = e => {
    const rows = e.attributions || [];
    if (rows.length === 0) return "\u2014";
    if (rows.length === 1) {
      const row = rows[0];
      // An attribution points at a person or a slice of the org chart, never
      // both — the API returns whichever it is.
      if (row.org_level_entity_id) {
        return row.org_level_entity?.name || `#${row.org_level_entity_id}`;
      }
      return nameById.get(row.user_id) || `#${row.user_id}`;
    }
    return `${rows.length} targets`;
  };

  const bearerSummary = e => {
    const rows = e.bearers || [];
    if (rows.length === 0) return "—";
    if (rows.length === 1) return bearerName(rows[0]);
    return `${rows.length} people`;
  };

  /** 0–100 for the fill bars; a cap of nothing means nothing to fill. */
  const fillPct = (spent, cap) =>
    cap ? Math.min(100, Math.max(0, (spent / cap) * 100)) : 0;

  const budgetPicker = (
    <Menu shadow="md" width={280} position="bottom-end">
      <Menu.Target>
        <button
          type="button"
          className="bt-hero-picker"
          aria-label="Choose budget"
        >
          <IconChevronDown size={18} stroke={2.5} />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          onClick={() => setBudgetFilter("all")}
          rightSection={
            <Text size="xs" c="dimmed">
              {money(totals.committed)}
            </Text>
          }
        >
          All budgets
        </Menu.Item>
        {budgetLines.length > 0 && <Menu.Divider />}
        {budgetLines.map(l => (
          <Menu.Item
            key={l.id}
            onClick={() => setBudgetFilter(String(l.id))}
            rightSection={
              <Text size="xs" c="dimmed">
                {money(l.committed)}
              </Text>
            }
          >
            {l.name}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconPlus size={14} />}
          onClick={() => setNewBudgetOpen(true)}
        >
          Add budget
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <Stack gap="lg" className="bt-sections">
      <div className="bt-stats">
        <div className="bt-stat bt-stat-hero">
          <div className="bt-stat-row">
            <span className="bt-hero-title">{heroLabel}</span>
            {budgetPicker}
          </div>
          <div className="bt-stat-row">
            <span className="bt-stat-value">{money(overview.heroSpent)}</span>
            {overview.heroCap != null && (
              <span className="bt-stat-of">of {money(overview.heroCap)}</span>
            )}
          </div>
          <div className="bt-bar">
            <div
              className="bt-bar-fill"
              style={{
                width: `${fillPct(overview.heroSpent, overview.heroCap)}%`,
              }}
            />
          </div>
          <div className="bt-stat-row">
            {overview.heroLeft == null ? (
              <span className="bt-stat-foot is-muted">No cap set</span>
            ) : overview.heroLeft < 0 ? (
              <span className="bt-stat-foot is-over">
                {money(-overview.heroLeft)} over
              </span>
            ) : (
              <span className="bt-stat-foot is-accent">
                {money(overview.heroLeft)} left
              </span>
            )}
          </div>
        </div>

        <div className="bt-stat">
          <span className="bt-stat-label">Budgeted</span>
          <span className="bt-stat-value">{money(overview.budgeted)}</span>
          <span className="bt-stat-hint">
            {budgetLines.length}{" "}
            {budgetLines.length === 1 ? "budget" : "budgets"}
            {" · "}
            {categories.length}{" "}
            {categories.length === 1 ? "category" : "categories"}
          </span>
        </div>

        <div className="bt-stat">
          <span className="bt-stat-label">Spent</span>
          <span className="bt-stat-value">{money(totals.committed)}</span>
          <div className="bt-bar">
            <div
              className="bt-bar-fill"
              style={{
                width: `${fillPct(totals.committed, overview.budgeted)}%`,
              }}
            />
          </div>
        </div>

        <div className="bt-stat">
          <span className="bt-stat-label">Unbudgeted</span>
          <span className="bt-stat-value">{overview.unbudgeted}</span>
          {overview.unbudgeted > 0 ? (
            <button
              type="button"
              className="bt-stat-link is-accent"
              onClick={() => setBudgetFilter("none")}
            >
              Review these <IconArrowRight size={13} />
            </button>
          ) : (
            <span className="bt-stat-hint">Every expense has a budget</span>
          )}
        </div>
      </div>

      <Modal
        opened={newBudgetOpen}
        onClose={() => setNewBudgetOpen(false)}
        title={<Text fw={700}>New budget</Text>}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Budget name"
            placeholder="Summer housing"
            value={newLine.name}
            onChange={ev => {
              const value = ev.currentTarget.value;
              setNewLine(l => ({ ...l, name: value }));
            }}
          />

          <MultiSelect
            label="Who can see it"
            description="You and company admins can always see it"
            placeholder="Pick people"
            searchable
            clearable
            required
            data={peopleOptions}
            value={newLine.viewerIds}
            onChange={value => setNewLine(l => ({ ...l, viewerIds: value }))}
          />

          <Group gap="lg" wrap="wrap">
            <Switch
              label="Transactions need approval"
              checked={newLine.requiresApproval}
              onChange={ev => {
                const checked = ev.currentTarget.checked;
                setNewLine(l => ({ ...l, requiresApproval: checked }));
              }}
            />
            <Switch
              label="Receipt required"
              checked={newLine.requiresReceipt}
              onChange={ev => {
                const checked = ev.currentTarget.checked;
                setNewLine(l => ({ ...l, requiresReceipt: checked }));
              }}
            />
          </Group>

          <Group justify="flex-end">
            <Button
              leftSection={<IconPlus size={16} />}
              loading={savingLine}
              onClick={saveBudgetLine}
            >
              Add budget
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        {/* Search anchors the left; actions stay grouped on the right. */}
        <TextInput
          placeholder="Search vendor or notes"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          style={{ width: 260 }}
        />

        <Group gap="sm">
          {view === "expenses" && (
            <Button
              variant={reconcileMode ? "light" : "default"}
              leftSection={<IconChecks size={16} />}
              onClick={() => setReconcileMode(r => !r)}
            >
              {reconcileMode ? "Exit reconcile" : "Reconcile mode"}
            </Button>
          )}

          <Button
            leftSection={
              mode === "single" ? <IconX size={16} /> : <IconPlus size={16} />
            }
            variant={mode === "single" ? "default" : "filled"}
            onClick={() => {
              setView("expenses");
              setComposerOpen(false);
              setNewBudgetOpen(false);
              setMode(m => (m === "single" ? null : "single"));
            }}
          >
            {mode === "single" ? "Cancel" : "Add expense"}
          </Button>

          {view === "expenses" && (
            <Button
              variant={mode === "bulk" ? "light" : "default"}
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setNewBudgetOpen(false);
                setMode(m => (m === "bulk" ? null : "bulk"));
              }}
            >
              {mode === "bulk" ? "Done" : "Bulk add"}
            </Button>
          )}
        </Group>
      </Group>

      <LogExpenseForm
        opened={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={load}
      />

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : view === "expenses" ? (
        <div className="bt-ledger">
          <div className="bt-ledger-scroll">
            <div
              className="bt-ledger-inner"
              ref={ledgerRef}
              style={{ "--bt-cols": template }}
            >
              <div className="bt-row bt-head">
                {HEAD_COLUMNS.map((col, i) => (
                  <div key={col.label || "open"} className="bt-head-cell">
                    <span className="bt-head-label">{col.label}</span>
                    {i < HEAD_COLUMNS.length - 1 && (
                      <span
                        className="bt-resize"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${col.label} column`}
                        onPointerDown={ev => startResize(ev, i)}
                        onDoubleClick={() => resetColumn(i)}
                      />
                    )}
                  </div>
                ))}
              </div>

              {mode && (
                <div
                  className="bt-row bt-tx bt-new"
                  onFocusCapture={ev => {
                    const cell = ev.target.closest("[data-col]");
                    setFocusCol(cell ? Number(cell.dataset.col) : null);
                  }}
                  onBlurCapture={() => setFocusCol(null)}
                >
                  <input
                    ref={firstFieldRef}
                    className={`bt-in bt-cap-left${
                      parseDateInput(draft.incurred_on) ? "" : " is-needed"
                    }`}
                    type="text"
                    inputMode="numeric"
                    placeholder="MM/DD/YYYY"
                    maxLength={10}
                    value={draft.incurred_on}
                    onChange={ev => {
                      const value = formatDateInput(ev.currentTarget.value);
                      setDraft(d => ({
                        ...d,
                        incurred_on: value,
                      }));
                    }}
                    onKeyDown={draftKeys}
                    aria-label="Date"
                  />
                  <input
                    className="bt-in bt-col-vendor"
                    data-col={1}
                    placeholder="Vendor"
                    value={draft.vendor}
                    onChange={ev => {
                      const value = ev.currentTarget.value;
                      setDraft(d => ({
                        ...d,
                        vendor: value,
                      }));
                    }}
                    onKeyDown={draftKeys}
                    aria-label="Vendor"
                  />
                  <Select
                    className={"bt-cell-select bt-col-source"}
                    data-col={2}
                    classNames={{ input: "bt-in" }}
                    searchable
                    nothingFoundMessage="Nothing found"
                    placeholder="Source…"
                    data={sourceOptions}
                    value={draft.sourceId ? String(draft.sourceId) : null}
                    onChange={value =>
                      setDraft(d => ({ ...d, sourceId: value }))
                    }
                    onKeyDown={draftKeys}
                    aria-label="Source"
                  />
                  <Select
                    className={`bt-cell-select bt-col-budget${draft.budgetLineId ? "" : " is-needed"}`}
                    data-col={3}
                    classNames={{ input: "bt-in" }}
                    searchable
                    nothingFoundMessage="Type a name to create one"
                    placeholder="Budget…"
                    data={budgetOptions}
                    searchValue={budgetSearch}
                    onSearchChange={setBudgetSearch}
                    value={
                      draft.budgetLineId ? String(draft.budgetLineId) : null
                    }
                    onChange={value => {
                      if (value === CREATE_BUDGET) {
                        createBudgetInline(budgetSearch);
                        return;
                      }
                      setDraft(d => ({ ...d, budgetLineId: value }));
                    }}
                    onKeyDown={draftKeys}
                    aria-label="Budget"
                  />
                  <Select
                    className={`bt-cell-select bt-col-category${draft.categoryId ? "" : " is-needed"}`}
                    data-col={4}
                    classNames={{ input: "bt-in" }}
                    searchable
                    nothingFoundMessage="Nothing found"
                    placeholder="Category…"
                    data={categoryOptions}
                    value={draft.categoryId ? String(draft.categoryId) : null}
                    onChange={value =>
                      setDraft(d => ({ ...d, categoryId: value }))
                    }
                    onKeyDown={draftKeys}
                    aria-label="Category"
                  />
                  <input
                    className="bt-in bt-col-description"
                    data-col={5}
                    placeholder="Description"
                    value={draft.description}
                    onChange={ev => {
                      const value = ev.currentTarget.value;
                      setDraft(d => ({
                        ...d,
                        description: value,
                      }));
                    }}
                    onKeyDown={draftKeys}
                    aria-label="Description"
                  />
                  {/* Several bearers split the cost evenly between them; the
                      exact per-person amounts are settled in the full form. */}
                  <MultiSelect
                    className={"bt-cell-select bt-col-expensed"}
                    data-col={6}
                    classNames={{
                      input: "bt-in",
                      pillsList: "bt-pills",
                      pill: "bt-pill",
                    }}
                    searchable
                    hidePickedOptions
                    nothingFoundMessage="Nothing found"
                    placeholder={draft.bearers.length ? "" : "Company"}
                    data={partyOptions}
                    value={draft.bearers}
                    onChange={value =>
                      setDraft(d => ({ ...d, bearers: value }))
                    }
                    onKeyDown={draftKeys}
                    aria-label="Expensed to"
                  />
                  <MultiSelect
                    className="bt-cell-select bt-col-spenton"
                    data-col={7}
                    classNames={{
                      input: "bt-in",
                      pillsList: "bt-pills",
                      pill: "bt-pill",
                    }}
                    searchable
                    hidePickedOptions
                    nothingFoundMessage="Nothing found"
                    placeholder={draft.spentOn.length ? "" : "\u2014"}
                    data={spentOnOptions}
                    value={draft.spentOn}
                    onChange={value =>
                      setDraft(d => ({ ...d, spentOn: value }))
                    }
                    onKeyDown={draftKeys}
                    aria-label="Spent on"
                  />
                  <input
                    className={`bt-in bt-in-amount${
                      parseAmountInput(draft.amount) ? "" : " is-needed"
                    }`}
                    type="text"
                    inputMode="decimal"
                    placeholder="$0.00"
                    value={draft.amount}
                    onChange={ev => {
                      const value = formatAmountInput(ev.currentTarget.value);
                      setDraft(d => ({
                        ...d,
                        amount: value,
                      }));
                    }}
                    onKeyDown={draftKeys}
                    aria-label="Amount"
                  />
                  <div className="bt-receipt-cell" />
                  <div className="bt-state bt-state-actions bt-cap-status">
                    <Button
                      size="compact-xs"
                      loading={savingRow}
                      onClick={saveDraft}
                    >
                      Save
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      onClick={cancelDraft}
                    >
                      Discard
                    </Button>
                  </div>
                  <div className="bt-open-cell bt-cap-open" />
                </div>
              )}

              {visibleExpenses.map(e => {
                const status = statusOf(e);
                return (
                  <div
                    key={e.id}
                    className={`bt-row bt-tx${
                      e.approval_status === "VOID" ? " is-void" : ""
                    }`}
                  >
                    <div className="bt-date bt-cap-left">
                      {formatDayShort(e.incurred_on)}
                    </div>

                    <div className="bt-vendor">
                      <span
                        className="bt-avatar"
                        style={{ background: avatarColor(e.vendor) }}
                      >
                        {vendorInitials(e.vendor)}
                      </span>
                      <div className="bt-vendor-text">
                        <div className="bt-vendor-name">
                          {e.vendor || "Untitled expense"}
                        </div>
                      </div>
                    </div>

                    <div
                      className={`bt-meta${
                        e.payment_source?.name ? "" : " is-muted"
                      }`}
                    >
                      <span className="bt-meta-icon">{"\u25A4"}</span>
                      <span>
                        {e.payment_source
                          ? `${e.payment_source.name}${
                              e.payment_source.last_four
                                ? ` \u2022\u2022${e.payment_source.last_four}`
                                : ""
                            }`
                          : "\u2014"}
                      </span>
                    </div>

                    <div
                      className={`bt-meta${
                        e.budget_line?.name ? "" : " is-muted"
                      }`}
                    >
                      {e.budget_line?.name && (
                        <span className="bt-meta-icon is-budget">
                          {"\u25CF"}
                        </span>
                      )}
                      <span>{e.budget_line?.name || "Unbudgeted"}</span>
                    </div>

                    <div
                      className={`bt-meta${
                        e.budget_category?.name ? "" : " is-empty"
                      }`}
                    >
                      <span className="bt-meta-icon">
                        {e.budget_category?.name ? "$" : "?"}
                      </span>
                      <span>{e.budget_category?.name || "Uncategorized"}</span>
                    </div>

                    <div
                      className={`bt-desc${e.description ? "" : " is-empty"}`}
                      title={e.description || undefined}
                    >
                      {e.description || "\u2014"}
                    </div>

                    <div className="bt-meta">
                      <span className="bt-meta-icon is-party">
                        {(e.bearers || []).length > 1 ? "\u2687" : "\u25CF"}
                      </span>
                      <span>{bearerSummary(e)}</span>
                    </div>

                    <div
                      className={`bt-meta${
                        (e.attributions || []).length ? "" : " is-muted"
                      }`}
                    >
                      {(e.attributions || []).length > 0 && (
                        <span className="bt-meta-icon is-attributed">
                          {"\u25CF"}
                        </span>
                      )}
                      <span>{attributionSummary(e)}</span>
                    </div>

                    <div className="bt-amount">{money(e.amount)}</div>

                    <div className="bt-receipt-cell">
                      <Tooltip
                        label={e.receipt_s3_key ? "View receipt" : "No receipt"}
                        withArrow
                      >
                        <button
                          type="button"
                          className={`bt-receipt${
                            e.receipt_s3_key ? " has-file" : ""
                          }`}
                          disabled={!e.receipt_s3_key}
                          onClick={() => openReceipt(e.id)}
                          aria-label={
                            e.receipt_s3_key
                              ? `View receipt for ${e.vendor || "expense"}`
                              : "No receipt attached"
                          }
                        >
                          <IconReceipt size={26} />
                        </button>
                      </Tooltip>
                    </div>

                    <div className="bt-state bt-cap-status">
                      {status.cls === "is-void" ? (
                        <span className={`bt-status ${status.cls}`}>
                          {status.label}
                        </span>
                      ) : (
                        <Menu position="bottom-end" shadow="md" width={200}>
                          <Menu.Target>
                            <button
                              type="button"
                              className={`bt-status ${status.cls}`}
                              aria-label={`${status.label} — change status for ${
                                e.vendor || "expense"
                              }`}
                            >
                              {status.label}
                            </button>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {(e.approval_status === "DRAFT" ||
                              e.approval_status === "REJECTED") && (
                              <Menu.Item
                                leftSection={<IconSend size={14} />}
                                disabled={busy}
                                onClick={() =>
                                  act(
                                    `/v1/spend/expenses/${e.id}/submit`,
                                    null,
                                    "Sent for approval."
                                  )
                                }
                              >
                                Submit for approval
                              </Menu.Item>
                            )}
                            {e.approval_status === "SUBMITTED" && (
                              <>
                                <Menu.Item
                                  leftSection={<IconCheck size={14} />}
                                  disabled={busy}
                                  onClick={() =>
                                    act(
                                      `/v1/spend/expenses/${e.id}/approve`,
                                      null,
                                      "Approved."
                                    )
                                  }
                                >
                                  Approve
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconX size={14} />}
                                  color="red"
                                  disabled={busy}
                                  onClick={() => rejectExpense(e.id)}
                                >
                                  Reject
                                </Menu.Item>
                              </>
                            )}
                            {e.approval_status === "APPROVED" &&
                              !e.reconciled_at && (
                                <Menu.Item
                                  leftSection={<IconChecks size={14} />}
                                  disabled={busy}
                                  onClick={() =>
                                    act(
                                      `/v1/spend/expenses/${e.id}/reconcile`,
                                      null,
                                      "Reconciled."
                                    )
                                  }
                                >
                                  Mark reconciled
                                </Menu.Item>
                              )}
                            {e.reconciled_at && (
                              <Menu.Item
                                disabled={busy}
                                onClick={() =>
                                  act(
                                    `/v1/spend/expenses/${e.id}/unreconcile`,
                                    null,
                                    "Reconciliation undone."
                                  )
                                }
                              >
                                Undo reconcile
                              </Menu.Item>
                            )}
                            {e.payment_status === "UNPAID" &&
                              e.approval_status !== "VOID" && (
                                <Menu.Item
                                  disabled={busy}
                                  onClick={() =>
                                    act(
                                      `/v1/spend/expenses/${e.id}/mark-paid`,
                                      {},
                                      "Marked paid."
                                    )
                                  }
                                >
                                  Mark paid
                                </Menu.Item>
                              )}
                            {e.approval_status !== "VOID" && (
                              <>
                                <Menu.Divider />
                                <Menu.Item
                                  color="red"
                                  disabled={busy}
                                  onClick={() => voidExpense(e.id)}
                                >
                                  Void
                                </Menu.Item>
                              </>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </div>
                    <div className="bt-open-cell bt-cap-open">
                      {/* Opens the expense's own view — not built yet, so this
                          is the affordance only. */}
                      <button
                        type="button"
                        className="bt-open"
                        aria-label={`Open ${e.vendor || "expense"}`}
                      >
                        <IconChevronRight size={17} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {visibleExpenses.length === 0 && !mode && (
                <div className="bt-empty">
                  <IconReceipt size={26} opacity={0.4} />
                  <Text c="dimmed" mt="xs">
                    {expenses.length === 0
                      ? "No expenses yet — hit Add expense to type one in."
                      : "Nothing filed here yet."}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : view === "owed" ? (
        ledger.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">Nobody owes anything right now.</Text>
          </Center>
        ) : (
          <Stack gap="md">
            <Table.ScrollContainer minWidth={700}>
              <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Person</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Owes</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Is owed</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Net</Table.Th>
                    <Table.Th>Oldest</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ledger.map(r => (
                    <Table.Tr key={r.user_id}>
                      <Table.Td>
                        <Text fw={500}>{fullName(r)}</Text>
                      </Table.Td>
                      <Table.Td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {money(r.owed_by_user)}
                      </Table.Td>
                      <Table.Td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {money(r.owed_to_user)}
                      </Table.Td>
                      <Table.Td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        <Text
                          fw={700}
                          c={Number(r.net) > 0 ? "red.7" : "green.7"}
                        >
                          {money(r.net)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{r.oldest_pending_on || "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Text size="sm" fw={600}>
              Unsettled slices
            </Text>
            <Table.ScrollContainer minWidth={700}>
              <Table verticalSpacing="xs">
                <Table.Tbody>
                  {expenses
                    .filter(e => e.approval_status === "APPROVED")
                    .flatMap(e =>
                      (e.bearers || [])
                        .filter(b => b.settlement_status === "PENDING")
                        .map(b => (
                          <Table.Tr key={`${e.id}-${b.id}`}>
                            <Table.Td>
                              <Text size="sm">
                                {e.vendor || `Expense #${e.id}`}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {e.incurred_on}
                              </Text>
                            </Table.Td>
                            <Table.Td>{bearerName(b)}</Table.Td>
                            <Table.Td
                              style={{
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {money(b.amount)}
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              <Tooltip label="Record how this was squared up">
                                <Button
                                  size="xs"
                                  variant="light"
                                  disabled={busy}
                                  onClick={() =>
                                    setSettleTarget({ expense: e, bearer: b })
                                  }
                                >
                                  Settle
                                </Button>
                              </Tooltip>
                            </Table.Td>
                          </Table.Tr>
                        ))
                    )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        )
      ) : byRep.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">Nothing has been spent on a rep yet.</Text>
        </Center>
      ) : (
        <Table.ScrollContainer minWidth={600}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Rep</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Cost to date</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Expenses</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {byRep.map(r => (
                <Table.Tr key={r.user_id}>
                  <Table.Td>
                    <Text fw={500}>{fullName(r)}</Text>
                  </Table.Td>
                  <Table.Td
                    style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    <Text fw={700}>{money(r.total)}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {r.expense_count}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={!!settleTarget}
        onClose={() => setSettleTarget(null)}
        title={<Text fw={700}>Settle this slice</Text>}
        radius="md"
      >
        {settleTarget && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {money(settleTarget.bearer.amount)} on{" "}
              {settleTarget.expense.vendor ||
                `expense #${settleTarget.expense.id}`}
              .
            </Text>
            <Select
              label="How was it squared up?"
              data={SETTLEMENT_METHODS}
              value={settleMethod}
              onChange={setSettleMethod}
            />
            {settleMethod === "PAYROLL_DEDUCTION" &&
              !settleTarget.expense.selling_year_id && (
                <Text size="xs" c="red.7">
                  This expense has no selling year, so it can&rsquo;t be taken
                  out of pay. Pick another method.
                </Text>
              )}
            <Textarea
              label="Note"
              placeholder="Optional"
              autosize
              minRows={2}
              value={settleNote}
              onChange={e => setSettleNote(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setSettleTarget(null)}>
                Cancel
              </Button>
              <Button onClick={confirmSettle} loading={busy}>
                Mark settled
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

export default BudgetTab;
